import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';
// Import shared components
import { H3, ListingLink } from '../../../../components';

// Import modules from this directory
import css from './EditListingEnchanedServicesPanel.module.css';
import { createSlug } from '../../../../util/urlHelpers';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import EditListingEnchanedServicesForm from './EditListingEnchanedServicesForm';
import {
  getProfileVideos,
  getProfileVideoURL,
  getTalentPlus,
  getUnlockableContent,
} from '../../../../util/listingDataExtraction';
import { filterWithoutUserType, isArrayLength } from '../../../../util/genericHelpers';
import { showErrorToast } from '../../../../util/toast';
import { createResourceLocatorString } from '../../../../util/routes';
import EditListingLayoutSideNav from '../EditListingLayoutSideNav/EditListingLayoutSideNav';
import { mapFilteredServices } from '../EditListingMoreInfoPanel/EditListingMoreInfoPanel';
import { useSelector } from 'react-redux';
import { userSelector } from '../../../../ducks/user.duck';
import { getUserSubscriptionLookupkeys, getUserType } from '../../../../util/userDataExtraction';
import { DIRECT_MESSAGE } from '../../../../util/constants';

const getInitialValues = listing => {
  const videos = getProfileVideos(listing); // Retrieves the profile videos
  const videoUrls = (isArrayLength(videos) && [...(getProfileVideoURL(videos) || [])]) || []; // Extracts the URLs from the videos
  const talentPlus = getTalentPlus(listing);
  const unlockableContent = getUnlockableContent(listing);
  return {
    profileVideos: videoUrls,
    talentPlus,
    subscriptionType: 'Recurring',
    unlockableContent,
  };
};

const EditListingEnchanedServicesPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    subscriptionPrices,
    onManageDisableScrolling,
    viewport,
  } = props;

  const existingProfileVideos = getProfileVideos(listing);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const listingId = listing?.id;
  const listingTitle = listing?.attributes?.title;
  const slug = createSlug(listingTitle);
  const classes = classNames(rootClassName || css.root, className);
  const initialValues = getInitialValues(listing);
  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;

  const currentUser = useSelector(userSelector);

  const subscriptionMaybe =
    isArrayLength(subscriptionPrices) && filterWithoutUserType(subscriptionPrices);

  const lookupKeys = getUserSubscriptionLookupkeys(currentUser);

  const userType = getUserType(currentUser);

  const handleSubmit = async (values, form, shouldSkipNextTab) => {
    setUploadInProgress(true);

    try {
      const { talentPlus = [], unlockableContent = {} } = values;
      const enhancedServicesSubTypes =
        (isArrayLength(talentPlus) &&
          talentPlus
            ?.filter(item => item?.selected === true && item?.type)
            .map(item => item.type)) ||
        [];
      const isDirectMessage =
        isArrayLength(talentPlus) &&
        talentPlus?.some(item => item?.selected === true && item?.type === DIRECT_MESSAGE);
      const unlockableTypes = [
        ...(isArrayLength(unlockableContent.photos) ? ['Photos'] : []),
        ...(isArrayLength(unlockableContent.posts) ? ['Posts'] : []),
        ...(isArrayLength(unlockableContent.videos) ? ['Videos'] : []),
        ...(isDirectMessage ? ['Direct Message'] : []),
      ];
      onSubmit({
        publicData: {
          talentPlus: mapFilteredServices(talentPlus),
          unlockableContent,
          ...(isArrayLength(enhancedServicesSubTypes) && {
            enhancedServicesSubTypes,
            isEnhancedServices: true,
          }),
          ...(isArrayLength(unlockableTypes) && { unlockableTypes }),
        },
      });

      if (!shouldSkipNextTab) {
        setTimeout(() => {
          history.push(
            createResourceLocatorString('ListingPage', routeConfiguration, {
              id: listingId?.uuid,
              slug,
            })
          );
        }, 1000);
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setUploadInProgress(false);
    }
  };

  return (
    <div className={classes}>
      <div className={css.contentLayoutWrapper}>
        <EditListingLayoutSideNav listing={listing} />
        <div className={css.detailContent}>
          <H3 as="h1">
            {isPublished ? (
              <FormattedMessage
                id="EditListingEnchanedServicesPanel.title"
                values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
              />
            ) : (
              <FormattedMessage
                id="EditListingEnchanedServicesPanel.createListingTitle"
                values={{ lineBreak: <br /> }}
              />
            )}
          </H3>

          <p
            className={css.detailDescription}
            dangerouslySetInnerHTML={{
              __html: `
      <strong>Get started offering Enhanced Services in a couple simple steps!</strong><br/><br/>
      1) Expand each service and place a check mark next to the services you wish to offer.<br/>
      2) Enter the price you would like to charge for each service.
    `,
            }}
          ></p>

          <EditListingEnchanedServicesForm
            className={css.form}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            updateInProgress={updateInProgress}
            saveActionMsg={submitButtonText}
            disabled={disabled || uploadInProgress}
            ready={ready}
            updated={panelUpdated}
            userType={userType}
            subscriptionMaybe={subscriptionMaybe}
            lookupKeys={lookupKeys}
            onLiveSubmit={(values, form) => handleSubmit(values, form, true)}
            viewport={viewport}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </div>
      </div>
    </div>
  );
};

const { func, object, string, bool } = PropTypes;

EditListingEnchanedServicesPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingEnchanedServicesPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingEnchanedServicesPanel;
